import Card from "../../../Card/Card";
import { Button, ButtonOption } from '../../../Fundamentals/Button';
import SkynetIcon from '../../../Fundamentals/SkynetIcon';
import { dateUtils } from '../../../../Utils';
import { useTranslation } from "react-i18next";

export function VossDiscountOptionsCard({
    offer,
    dryRunResult,
    selections,
    onOpenDiscountModal,
    onUpdateSelection
}) {
    const { t } = useTranslation();

    const onRemoveDiscount = (discountAgreementId) => {
        onUpdateSelection('discountAgreements',
            selections.discountAgreements.filter(d => d.discountAgreementId !== discountAgreementId)
        );
    };

    const selectedDiscounts = dryRunResult?.discounts ?? [];

    // The order only carries the discounts it adds, so those are the only ones it can withdraw.
    // Discounts already live on the subscription are removed from the subscription page instead.
    const isRemovable = (discountAgreementId) => (selections.discountAgreements ?? [])
        .some(d => d.discountAgreementId === discountAgreementId);

    // Voss only echoes discounts back once they apply to the subscription, so a discount added
    // alongside a deferred tier change is missing from the dry run. Show what was selected instead,
    // resolving the name from the same offer list the add modal is fed.
    const pendingDiscounts = (selections.discountAgreements ?? [])
        .filter(d => !selectedDiscounts.some(sd => sd.discountAgreementId === d.discountAgreementId))
        .map(d => ({
            discountAgreementId: d.discountAgreementId,
            name: offer?.discounts?.find(od => od.id === d.discountAgreementId)?.name,
            months: d.months
        }));

    return (
        <Card>
            <Card.Header>
                <Card.Header.Title>{t('vossDiscountOptionsCard.discountAgreements')}</Card.Header.Title>
                <Card.Header.Buttons>
                    <Button
                        option={ButtonOption.PRIMARY}
                        onClick={onOpenDiscountModal}
                    >
                        {t('vossDiscountOptionsCard.addDiscount')}
                    </Button>
                </Card.Header.Buttons>
            </Card.Header>
            <Card.Content>
                <table className="table table-stripe skynet-table-no-divider mb-0">
                    <thead>
                        <tr>
                            <th>{t('common.name')}</th>
                            <th>{t('common.startdate')}</th>
                            <th>{t('common.enddate')}</th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        {selectedDiscounts.length === 0 && pendingDiscounts.length === 0 && (
                            <tr>
                                <td colSpan={4}><i>{t('vossDiscountOptionsCard.noDiscountAgreements')}</i></td>
                            </tr>
                        )}
                        {selectedDiscounts.map((discountAgreement, index) => (
                            <tr key={index}>
                                <td>{discountAgreement.name}</td>
                                <td>{dateUtils.formatDate(discountAgreement.startDate)}</td>
                                <td>{dateUtils.formatDate(discountAgreement.endDate)}</td>
                                <td>
                                    {isRemovable(discountAgreement.discountAgreementId) && (
                                        <div className="float-right">
                                            <SkynetIcon
                                                icon={"vismaicon vismaicon-cancel-circle"}
                                                onClick={() => onRemoveDiscount(discountAgreement.discountAgreementId)}
                                            />
                                        </div>
                                    )}
                                </td>
                            </tr>
                        ))}
                        {pendingDiscounts.map((discountAgreement) => (
                            <tr key={`pending-${discountAgreement.discountAgreementId}`}>
                                <td>{discountAgreement.name}</td>
                                <td colSpan={2}>
                                    <em className="text-muted">
                                        {t('vossDiscountOptionsCard.appliesFromNextPeriod')}
                                        {discountAgreement.months
                                            ? ` · ${t('vossDiscountOptions.monthCount', { count: discountAgreement.months })}`
                                            : ''}
                                    </em>
                                </td>
                                <td>
                                    <div className="float-right">
                                        <SkynetIcon
                                            icon={"vismaicon vismaicon-cancel-circle"}
                                            onClick={() => onRemoveDiscount(discountAgreement.discountAgreementId)}
                                        />
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </Card.Content>
        </Card>
    );
}